import { z } from 'zod';

const trimmed = (max: number) => z.string().trim().max(max);

export const restaurantSearchSchema = z.object({
  name: trimmed(120).min(2, 'Enter at least 2 characters'),
  city: trimmed(80).optional().default(''),
  region: trimmed(80).optional().default(''),
  country: trimmed(80).optional().default(''),
});
export type RestaurantSearchInput = z.input<typeof restaurantSearchSchema>;

/**
 * Places autocomplete. `session` groups keystrokes + the final pick into one
 * billable Google session (per Places API pricing).
 */
const sessionToken = z
  .string()
  .regex(/^[\w-]{8,64}$/, 'Invalid session token')
  .optional();

export const placeAutocompleteSchema = z.object({
  q: z.string().trim().min(2, 'Type at least 2 characters').max(120),
  session: sessionToken,
});

export const placeSelectSchema = z.object({
  placeId: z
    .string()
    .trim()
    .regex(/^[\w-]{3,300}$/, 'Invalid place id'),
  session: sessionToken,
});

/** The single "grade my restaurant" form: address fields plus an optional website. */
export const gradeSearchSchema = restaurantSearchSchema.extend({
  website: z.string().trim().max(2048, 'Website address is too long').optional().default(''),
});
export type GradeSearchInput = z.input<typeof gradeSearchSchema>;

export const utmSchema = z
  .object({
    utm_source: trimmed(200).optional(),
    utm_medium: trimmed(200).optional(),
    utm_campaign: trimmed(200).optional(),
    utm_term: trimmed(200).optional(),
    utm_content: trimmed(200).optional(),
  })
  .partial();
export type UtmParams = z.infer<typeof utmSchema>;

export const attributionSchema = z.object({
  utm: utmSchema.optional(),
  referrer: trimmed(500).optional(),
  landingPage: trimmed(500).optional(),
});
export type Attribution = z.infer<typeof attributionSchema>;

export const websiteUrlSchema = z
  .string()
  .trim()
  .min(3, 'Enter a website address')
  .max(2048, 'Website address is too long');

export const websiteCheckSchema = z.object({ url: websiteUrlSchema });

export const createScanSchema = z.object({
  restaurantId: z.string().regex(/^[a-f\d]{24}$/i, 'Invalid restaurant id'),
  websiteUrl: websiteUrlSchema,
  attribution: attributionSchema.optional(),
});
export type CreateScanInput = z.infer<typeof createScanSchema>;

export const leadSchema = z.object({
  name: trimmed(120).min(1, 'Name is required'),
  email: z.string().trim().toLowerCase().email('Enter a valid email').max(254),
  phone: trimmed(40).optional().or(z.literal('')),
  restaurantName: trimmed(160).optional().or(z.literal('')),
  website: trimmed(2048).optional().or(z.literal('')),
  scanId: z
    .string()
    .regex(/^[a-f\d]{24}$/i)
    .optional(),
  consent: z.literal(true, { message: 'Please agree so we can contact you' }),
  attribution: attributionSchema.optional(),
});
export type LeadInput = z.input<typeof leadSchema>;

export const passwordSchema = z
  .string()
  .min(10, 'Use at least 10 characters')
  .max(128, 'Password is too long');

export const registerSchema = z.object({
  name: trimmed(120).min(1, 'Name is required'),
  email: z.string().trim().toLowerCase().email('Enter a valid email').max(254),
  password: passwordSchema,
});
export type RegisterInput = z.infer<typeof registerSchema>;

export const googleAuthSchema = z.object({
  /** ID token ("credential") returned by Google Identity Services. */
  credential: z.string().min(20).max(4096),
});
export type GoogleAuthInput = z.infer<typeof googleAuthSchema>;

export const loginSchema = z.object({
  email: z.string().trim().toLowerCase().email('Enter a valid email').max(254),
  password: z.string().min(1, 'Password is required').max(128),
});
export type LoginInput = z.infer<typeof loginSchema>;
